// The one searchable pick list (the combobox of the system): typing filters, arrows move, Enter picks. Field pickers, the command
// palette, the event choice and long selects are built on it.

import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from 'react';
import { textKey } from '@confdeck/core-shared';
import { Icon } from './icons.tsx';
import { cx } from './base.tsx';
import { CheckMark } from './controls.tsx';
import s from './pick.module.css';

export interface PickOption { id: string; label: string; render?: ReactNode; group?: string; hint?: string }

/**
 * The one pick list. Arrow keys move, Enter picks, typing filters. With `multiple`, picking toggles
 * and the list stays open; otherwise it calls `onDone`.
 */
export function PickList({ options, selected, onPick, multiple, placeholder = 'Поиск…', onCreate, createLabel, footer, onDone, emptyText = 'Ничего не нашлось' }: {
  options: PickOption[] | ((q: string) => PickOption[]);
  selected: string[];
  onPick: (id: string) => void;
  multiple?: boolean;
  placeholder?: string;
  onCreate?: (label: string) => void;
  /** What the create row says before the typed name («Создать в «Задачи»»). */
  createLabel?: string;
  footer?: ReactNode;
  onDone?: () => void;
  emptyText?: string;
}) {
  const [q, setQ] = useState('');
  const id = useId();
  const [active, setActive] = useState(0);
  const listRef = useRef<HTMLDivElement>(null);
  const list = useMemo(() => {
    if (typeof options === 'function') return options(q);
    const k = textKey(q);
    return k ? options.filter((o) => textKey(o.label).includes(k)) : options;
  }, [options, q]);
  const canCreate = !!onCreate && q.trim() && !list.some((o) => textKey(o.label) === textKey(q));
  const total = list.length + (canCreate ? 1 : 0);
  useEffect(() => setActive(0), [q]);
  useEffect(() => { listRef.current?.querySelector(`[data-i="${active}"]`)?.scrollIntoView({ block: 'nearest' }); }, [active]);

  const pick = (i: number) => {
    if (i === list.length && canCreate) { onCreate!(q.trim()); setQ(''); if (!multiple) onDone?.(); return; }
    const o = list[i];
    if (!o) return;
    onPick(o.id);
    if (!multiple) onDone?.();
  };

  let lastGroup: string | undefined;
  return (
    <div className={s.pick} onKeyDown={(e) => {
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive((a) => Math.min(total - 1, a + 1)); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((a) => Math.max(0, a - 1)); }
      else if (e.key === 'Enter') { e.preventDefault(); pick(active); }
    }}>
      <div className={s.pickSearch}>
        <Icon name="search" size={14} />
        <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder} aria-label={placeholder}
          role="combobox" aria-expanded="true" aria-controls={id} aria-autocomplete="list" aria-activedescendant={total ? `${id}-${active}` : undefined} />
      </div>
      <div className={s.pickList} ref={listRef} role="listbox" id={id} aria-multiselectable={multiple}>
        {list.map((o, i) => {
          const header = o.group && o.group !== lastGroup ? <div className={s.pickGroup}>{o.group}</div> : null;
          lastGroup = o.group;
          const on = selected.includes(o.id);
          return (
            <div key={o.id}>
              {header}
              <div data-i={i} id={`${id}-${i}`} role="option" aria-selected={on} className={cx(s.pickItem, i === active && s.pickActive)}
                onMouseMove={() => setActive(i)} onMouseDown={(e) => { e.preventDefault(); pick(i); }}>
                {multiple && <CheckMark checked={on} />}
                <span className={s.pickLabel}>{o.render ?? o.label}</span>
                {o.hint && <span className={s.pickHint}>{o.hint}</span>}
                {!multiple && on && <Icon name="tick" size={14} />}
              </div>
            </div>
          );
        })}
        {canCreate && (
          <div data-i={list.length} id={`${id}-${list.length}`} role="option" aria-selected={false} className={cx(s.pickItem, active === list.length && s.pickActive)} onMouseMove={() => setActive(list.length)} onMouseDown={(e) => { e.preventDefault(); pick(list.length); }}>
            <Icon name="plus" size={14} /><span className={s.pickLabel}>{createLabel ? `${createLabel}: «${q.trim()}»` : `Создать «${q.trim()}»`}</span>
          </div>
        )}
        {!total && <div className={s.pickEmpty}>{emptyText}</div>}
      </div>
      {footer}
    </div>
  );
}

